import React, { useRef, useState } from 'react';
import { Box, ModuleBase, Text, Stack, ResponsiveImage, AnimatePresence, Link } from '@/components';
import classNames from 'classnames';
import { fadeIn, listingGridMotion } from '@/animations';
import { SideBySideModuleModel } from './SideBySideModule';
import ArrowRightRound from '@/assets/arrowRightRound.svg';
import { motion } from 'framer-motion';

const AdvisoryHowCanWeHelpYou = (data: SideBySideModuleModel) => {
  const { alignment, preTitle, title, description, cards, image, link1 } = data.props;

  const [animation, setAnimation] = useState<object | undefined>(undefined);
  const [activeDescription, setActiveDescription] = useState<string | null>(description || null);
  const [activeDescriptionIndex, setActiveDescriptionIndex] = useState<number>(-1);
  const nextDescription = useRef<string | null>(null);

  const _setActiveDescription = (description: string | null, index: number) => {
    if (description !== activeDescription) {
      setActiveDescriptionIndex(index);
      setActiveDescription(null);
      nextDescription.current = description;
    }
  };

  const cardsObj = cards && (
    <div className="mt-5 flex-col gap-5 lg:mt-10">
      {cards.map((card, index) => (
        <Box
          key={index}
          className="border-from-bg group flex cursor-pointer flex-row items-center justify-between gap-1 border-b border-solid py-3 last:border-b-0 lg:py-4 lg:pr-9"
          onMouseEnter={() => _setActiveDescription(card.text || '', index)}
        >
          <p className="text-style-h5 text-[14px] sm:text-[20px] md:text-[26px] xl:w-3/4">{card.title}</p>
          <ArrowRightRound
            className={classNames(
              'h-8 w-8 shrink-0 opacity-0 transition-all duration-500 group-hover:opacity-100 group-focus:opacity-100 md:h-10 md:w-10',
              index === activeDescriptionIndex && 'pointer-coarse:opacity-100',
            )}
          />
        </Box>
      ))}
    </div>
  );

  return (
    <ModuleBase
      noPadding
      data={{ ...data, props: { ...data.props, backgroundColour: '#f9f2e1' } }}
      className={classNames(
        'AdvisoryHowCanWeHelpYou relative flex flex-col-reverse items-center lg:flex-row',
        alignment === 'Right' ? 'justify-end' : 'justify-start',
      )}
    >
      {image && (
        <Stack
          animation={animation}
          onAnimationComplete={() => setAnimation(listingGridMotion)}
          className="relative h-[380px] w-full overflow-hidden sm:h-[500px] lg:min-h-[622px] lg:w-1/2 xl:min-h-[854px]"
        >
          {image && image.desktopImage && (
            <ResponsiveImage
              animation={fadeIn}
              image={image.desktopImage}
              widths={{ xs: 425, sm: 650, md: 758, lg: 512, xl: 720, xxl: 950 }}
              heights={{ xs: 380, sm: 500, md: 500, lg: 622, xl: 850, xxl: 850 }}
              onClick={() => _setActiveDescription(description || '', -1)}
            />
          )}
          <div className="absolute bottom-0 left-0 bg-black/50 p-6 text-white lg:min-h-[330px] lg:p-10 xl:min-h-[310px]">
            <AnimatePresence onExitComplete={() => setActiveDescription(nextDescription.current)}>
              {activeDescription && (
                <motion.div
                  key={activeDescriptionIndex}
                  initial={{
                    x: -50,
                    opacity: 0,
                  }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{
                    x: -50,
                    opacity: 0,
                    transition: {
                      type: 'tween',
                      ease: 'easeOut',
                      duration: 0.2,
                    },
                  }}
                  transition={{
                    type: 'tween',
                    ease: 'easeOut',
                    duration: 0.5,
                  }}
                >
                  <div
                    className={classNames(activeDescription === description ? 'text-style-h3' : 'text-style-h5')}
                    dangerouslySetInnerHTML={{ __html: activeDescription }}
                  />
                  {link1 && activeDescription === description && (
                    <Link
                      className="btn primary v2 !bg-yellow !border-yellow !text-green mt-7 hover:!border-white hover:!bg-transparent hover:!text-white"
                      link={link1}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Stack>
      )}

      <div className="pointer-events-none left-0 flex w-full items-center pt-10 pb-5 lg:absolute lg:h-full lg:pt-0 lg:pb-0">
        <div
          className={classNames(
            'container flex lg:h-full lg:py-18',
            alignment === 'Right' ? 'lg:justify-start' : 'lg:justify-end',
          )}
        >
          <Stack
            className={classNames(
              'pointer-events-auto flex w-full flex-col lg:w-1/2 lg:justify-between',
              alignment === 'Right' ? '' : 'lg:pl-9',
            )}
          >
            <div className="flex flex-col gap-5">
              {preTitle && <Text className="text-style-tag uppercase">{preTitle}</Text>}
              {title && (
                <Text
                  className="text-style-h3 pr-10 md:pr-20 lg:pr-0 xl:w-9/12 xl:leading-[90%]"
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              )}
            </div>
            <div
              className="w-full pointer-coarse:hidden"
              onMouseLeave={() => _setActiveDescription(description || '', -1)}
            >
              {cardsObj}
            </div>
            <div className="w-full pointer-fine:hidden">{cardsObj}</div>
          </Stack>
        </div>
      </div>
    </ModuleBase>
  );
};

export default AdvisoryHowCanWeHelpYou;
